import {
  forwardRef,
  useState,
  type ComponentProps,
  type ElementType,
  type ReactElement,
  type ReactNode,
} from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  ChevronDown,
  ChevronUp,
  GraduationCap,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { SectionLabel } from '../layout/Typography';
import { splitByCategory, ChangelogCategorySection, ChangelogEntries } from './ChangelogEntry';
import { parseChangelogEntries } from './changelog-format';

/**
 * Paragraph treatments tuned for a clear reading hierarchy:
 * - `lead` — the release overview, a calm intro line beneath the title.
 * - `body` — secondary supporting copy (feature descriptions), de-emphasized so
 *   feature titles stay the primary anchor.
 * - changelog prose keeps the denser, foreground-weighted list treatment.
 */
export const RELEASE_PROSE_LEAD =
  'text-base leading-relaxed text-foreground/80 [&:not(:first-child)]:mt-3';
export const RELEASE_PROSE_BODY =
  'text-sm leading-relaxed text-muted-foreground [&:not(:first-child)]:mt-2';
export const RELEASE_PROSE_CHANGELOG = 'text-sm text-foreground leading-relaxed my-2';

/**
 * Markdown renderers shared by the release notification body and the changelog
 * page so both surfaces render release prose identically. `paragraphClassName`
 * lets callers pick the lead/body/changelog prose treatment.
 */
export function releaseMarkdownComponents(opts: { paragraphClassName?: string } = {}): Components {
  const paragraphClassName = opts.paragraphClassName ?? RELEASE_PROSE_CHANGELOG;
  return {
    h3: () => null as unknown as ReactElement,
    ul: ({ children }) => <ul className="space-y-0.5">{children}</ul>,
    li: ({ children }) => (
      <li className="flex items-start gap-2.5 rounded-lg px-3 py-1.5 text-sm text-foreground hover:bg-muted/30 transition-colors">
        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
        <span className="leading-relaxed">{children}</span>
      </li>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-primary underline underline-offset-2 hover:opacity-80 transition-opacity"
      >
        {children}
      </a>
    ),
    p: ({ children }) => <p className={paragraphClassName}>{children}</p>,
    code: ({ children }) => (
      <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{children}</code>
    ),
  };
}

export interface ReleaseSectionHeaderProps {
  children: ReactNode;
  count?: number;
  tone?: 'muted' | 'strong';
}

/**
 * Section heading for release content: a quiet uppercase label with an optional
 * count chip, giving each section ("Highlights", "What's new", "Changelog") a
 * consistent, scannable header across the notification and changelog surfaces.
 */
export function ReleaseSectionHeader({
  children,
  count,
  tone = 'strong',
}: ReleaseSectionHeaderProps) {
  return (
    <div className="flex items-center gap-2">
      <SectionLabel as="h3" tone={tone}>
        {children}
      </SectionLabel>
      {count != null && (
        <span className="rounded-full bg-muted px-1.5 py-px text-2xs font-medium tabular-nums text-muted-foreground">
          {count}
        </span>
      )}
    </div>
  );
}

export interface ReleaseSummaryProps {
  /** Optional markdown overview shown as a lead paragraph. */
  overview?: string;
  /** Up to four short, plain-text highlight bullets. */
  highlights?: string[];
  /** Spacing/layout class applied to the wrapper (e.g. `space-y-6`). */
  className?: string;
}

/**
 * The shared "what's in this release" summary: an optional markdown overview
 * lead followed by a "Highlights" bullet list. Rendered identically by the
 * in-app release notification (`ReleaseEventRenderer`) and the changelog page so
 * the two surfaces always match. Renders nothing when there is no overview and
 * no highlights.
 */
export function ReleaseSummary({ overview, highlights, className }: ReleaseSummaryProps) {
  const items = highlights ?? [];
  if (!overview && items.length === 0) return null;
  return (
    <div className={className} data-testid="release-summary">
      {overview ? (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={releaseMarkdownComponents({ paragraphClassName: RELEASE_PROSE_LEAD })}
        >
          {overview}
        </ReactMarkdown>
      ) : null}
      {items.length > 0 ? (
        <section className="space-y-3">
          <ReleaseSectionHeader count={items.length}>Highlights</ReleaseSectionHeader>
          <ul className="space-y-2">
            {items.map((highlight) => (
              <li
                key={highlight}
                className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground"
              >
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>{highlight}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** Semver-style scope of a release. Mirrors the notifications `ReleaseType`. */
export type ReleaseType = 'major' | 'minor' | 'patch' | 'hotfix';

/** A deep link from a release feature to an in-app guided tutorial. */
export interface ReleaseTutorialLink {
  label: string;
  href: string;
}

/** A high-level feature shipped in a release, with optional tutorial links. */
export interface ReleaseFeature {
  title: string;
  description?: string;
  tutorials?: ReleaseTutorialLink[];
}

/** A case study card linking out to a docs page. */
export interface ReleaseCaseStudy {
  title: string;
  description?: string;
  href: string;
}

const RELEASE_TYPE_LABEL: Record<ReleaseType, string> = {
  major: 'Major release',
  minor: 'Minor release',
  patch: 'Patch',
  hotfix: 'Hotfix',
};

const RELEASE_TYPE_VARIANT: Record<
  ReleaseType,
  'default' | 'secondary' | 'subtle' | 'destructive'
> = {
  major: 'default',
  minor: 'secondary',
  patch: 'subtle',
  hotfix: 'destructive',
};

export interface ReleaseTypeBadgeProps {
  releaseType: ReleaseType;
}

/**
 * Release-type chip ("Minor release", "Hotfix", …). Shared by the release
 * notification header and the releases page so the release type renders
 * identically across both surfaces.
 */
export function ReleaseTypeBadge({ releaseType }: ReleaseTypeBadgeProps) {
  return (
    <Badge variant={RELEASE_TYPE_VARIANT[releaseType]}>{RELEASE_TYPE_LABEL[releaseType]}</Badge>
  );
}

/**
 * Link component contract used by {@link ReleaseFeatures} and
 * {@link ReleaseCaseStudies}. Keeps this UI router-agnostic: callers in the SPA
 * pass react-router's `Link` (which takes a `to` prop); the default falls back
 * to a plain anchor so stories and tests render without a router.
 */
export type ReleaseLinkComponent = ElementType;

const AnchorLink = forwardRef<
  HTMLAnchorElement,
  { to: string } & Omit<ComponentProps<'a'>, 'href'>
>(function AnchorLink({ to, children, ...rest }, ref) {
  return (
    <a ref={ref} href={to} {...rest}>
      {children}
    </a>
  );
});

export interface ReleaseFeaturesProps {
  features: ReleaseFeature[];
  /** Link component for tutorial deep-links (defaults to a plain anchor). */
  linkComponent?: ReleaseLinkComponent;
  className?: string;
}

/**
 * The "What's new" section: one article per high-level feature, separated by
 * hairlines rather than boxed in cards, with an optional markdown description
 * and its tutorial deep-links. Renders nothing when there are no features.
 */
export function ReleaseFeatures({
  features,
  linkComponent: LinkComponent = AnchorLink,
  className = 'space-y-4',
}: ReleaseFeaturesProps) {
  if (features.length === 0) return null;
  return (
    <section className={className}>
      <ReleaseSectionHeader count={features.length}>What's new</ReleaseSectionHeader>
      <div className="divide-y divide-border/40">
        {features.map((feature) => (
          <article key={feature.title} className="space-y-2 py-4 first:pt-0 last:pb-0">
            <h4 className="text-base font-medium leading-snug tracking-tight text-foreground">
              {feature.title}
            </h4>
            {feature.description ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={releaseMarkdownComponents({ paragraphClassName: RELEASE_PROSE_BODY })}
              >
                {feature.description}
              </ReactMarkdown>
            ) : null}
            {feature.tutorials && feature.tutorials.length > 0 ? (
              <div className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
                {feature.tutorials.map((tutorial) => (
                  <LinkComponent
                    key={tutorial.href}
                    to={tutorial.href}
                    className="group inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:text-primary/80"
                  >
                    <GraduationCap className="icon-sm" aria-hidden="true" />
                    {tutorial.label}
                    <ArrowRight
                      className="icon-sm transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </LinkComponent>
                ))}
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

export interface ReleaseCaseStudiesProps {
  caseStudies: ReleaseCaseStudy[];
  /** Link component for the case-study cards (defaults to a plain anchor). */
  linkComponent?: ReleaseLinkComponent;
  className?: string;
}

/**
 * The "Case studies" section: a grid of quiet link rows, each opening a docs
 * page. Borderless by design — the hover fill is the affordance, so the cards
 * do not compete with the release content above them. Renders nothing when
 * there are no case studies.
 */
export function ReleaseCaseStudies({
  caseStudies,
  linkComponent: LinkComponent = AnchorLink,
  className = 'space-y-4',
}: ReleaseCaseStudiesProps) {
  if (caseStudies.length === 0) return null;
  return (
    <section className={className}>
      <ReleaseSectionHeader count={caseStudies.length}>Case studies</ReleaseSectionHeader>
      <div className="grid gap-1 sm:grid-cols-2">
        {caseStudies.map((caseStudy) => (
          <LinkComponent
            key={caseStudy.href}
            to={caseStudy.href}
            className="group -mx-3 flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-muted/40"
          >
            <BookOpen className="mt-0.5 icon-sm shrink-0 text-primary/80" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium leading-snug text-foreground">
                {caseStudy.title}
              </span>
              {caseStudy.description ? (
                <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                  {caseStudy.description}
                </span>
              ) : null}
            </span>
            <ArrowUpRight
              className="mt-0.5 icon-sm shrink-0 text-muted-foreground/40 transition-colors group-hover:text-foreground"
              aria-hidden="true"
            />
          </LinkComponent>
        ))}
      </div>
    </section>
  );
}

/** A long changelog (more list items than this) collapses behind "Show more". */
const CHANGELOG_COLLAPSE_ITEM_THRESHOLD = 8;

export interface ReleaseChangelogSectionProps {
  /** Markdown changelog body (release-please conventional-changelog format). */
  changelogSummary: string;
  /** Collapse a long changelog behind a "Show more" toggle. Default true. */
  collapsible?: boolean;
}

/**
 * The "Changelog" section: a muted header and the changelog body split into
 * conventional-changelog category sections, each badging its entries by product
 * area. A long changelog collapses behind a "Show more" toggle so a single
 * release never floods the surface.
 *
 * There is deliberately no "View full changelog" link. It pointed at the in-app
 * releases page, but the changelog that page shows is generated from a private
 * repo, so it is hidden until there is a changelog we can stand behind
 * publicly. Restore it by rendering `changelogUrl` again.
 */
export function ReleaseChangelogSection({
  changelogSummary,
  collapsible = true,
}: ReleaseChangelogSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const categories = splitByCategory(changelogSummary);
  const itemCount = parseChangelogEntries(changelogSummary).length;
  const canCollapse = collapsible && itemCount > CHANGELOG_COLLAPSE_ITEM_THRESHOLD;
  const collapsed = canCollapse && !expanded;

  const body =
    categories.length > 0 && categories[0].name !== '' ? (
      <div className="space-y-6">
        {categories.map((cat) => (
          <ChangelogCategorySection key={cat.name} name={cat.name}>
            <ChangelogEntries content={cat.content} />
          </ChangelogCategorySection>
        ))}
      </div>
    ) : (
      <ChangelogCategorySection name="">
        <ChangelogEntries content={changelogSummary} />
      </ChangelogCategorySection>
    );

  return (
    <section className="space-y-4">
      <ReleaseSectionHeader tone="muted" count={itemCount > 0 ? itemCount : undefined}>
        Changelog
      </ReleaseSectionHeader>
      <div
        className={cn(
          'relative transition-[max-height] duration-300 ease-out',
          collapsed && 'max-h-[22rem] overflow-hidden',
        )}
      >
        {body}
        {collapsed ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-28 items-end justify-center bg-gradient-to-t from-background via-background/90 to-transparent">
            <Button
              variant="ghost"
              size="sm"
              className="pointer-events-auto rounded-full border border-border/60 bg-background/80 text-muted-foreground backdrop-blur hover:text-foreground"
              onClick={() => setExpanded(true)}
            >
              <ChevronDown className="icon-sm" aria-hidden="true" />
              Show more
            </Button>
          </div>
        ) : null}
      </div>
      {canCollapse && expanded ? (
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(false)}
        >
          <ChevronUp className="icon-sm" aria-hidden="true" />
          Show less
        </Button>
      ) : null}
    </section>
  );
}

const SMALL_CHANGES_NOTE: Record<ReleaseType, string> = {
  major: 'This release ships major changes. See the full changelog below.',
  minor: 'This release adds improvements. See the full changelog below.',
  patch: 'This release contains small fixes and improvements.',
  hotfix: 'This release contains an urgent fix. See the full changelog below.',
};

export interface ReleaseDetailsProps {
  releaseType: ReleaseType;
  /** Optional short headline rendered above the overview. */
  title?: string;
  overview?: string;
  highlights?: string[];
  features?: ReleaseFeature[];
  caseStudies?: ReleaseCaseStudy[];
  /** Markdown changelog body. Omit (or empty) to hide the changelog section. */
  changelogSummary?: string;
  /** Link component for tutorial and case-study links. */
  linkComponent?: ReleaseLinkComponent;
  /** Collapse a long changelog behind a "Show more" toggle. Default true. */
  collapsibleChangelog?: boolean;
  /**
   * Show the release-type "small changes" note when there is no authored
   * content. Default true (notifications); the releases page passes false since
   * the changelog is always present right below.
   */
  showSmallChangesNote?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * The full release body shared by the in-app release notification
 * (`ReleaseEventRenderer`) and the releases page: an overview + "Highlights"
 * summary, high-level features (each deep-linking its tutorials), case-study
 * links, and the changelog entry. Sections are separated by whitespace and
 * their own labels rather than rules — the labels already mark the boundary,
 * and a divider between every one of them reads as a stack of boxes.
 * Rendering both surfaces from one component keeps their treatment identical.
 */
export function ReleaseDetails({
  releaseType,
  title,
  overview,
  highlights = [],
  features = [],
  caseStudies = [],
  changelogSummary,
  linkComponent,
  collapsibleChangelog = true,
  showSmallChangesNote = true,
  className,
  ...rest
}: ReleaseDetailsProps) {
  const summary = changelogSummary ?? '';
  const hasChangelog = summary.trim().length > 0;
  const hasAuthoredContent =
    Boolean(title) ||
    Boolean(overview) ||
    highlights.length > 0 ||
    features.length > 0 ||
    caseStudies.length > 0;

  const sections: { key: string; node: ReactNode }[] = [];

  if (title) {
    sections.push({
      key: 'title',
      node: <h3 className="text-xl font-semibold tracking-tight text-foreground">{title}</h3>,
    });
  }

  if (overview || highlights.length > 0) {
    sections.push({
      key: 'summary',
      node: <ReleaseSummary overview={overview} highlights={highlights} className="space-y-6" />,
    });
  } else if (!hasAuthoredContent && showSmallChangesNote) {
    sections.push({
      key: 'intro',
      node: <p className={RELEASE_PROSE_LEAD}>{SMALL_CHANGES_NOTE[releaseType]}</p>,
    });
  }

  if (features.length > 0) {
    sections.push({
      key: 'features',
      node: <ReleaseFeatures features={features} linkComponent={linkComponent} />,
    });
  }

  if (caseStudies.length > 0) {
    sections.push({
      key: 'caseStudies',
      node: <ReleaseCaseStudies caseStudies={caseStudies} linkComponent={linkComponent} />,
    });
  }

  if (hasChangelog) {
    sections.push({
      key: 'changelog',
      node: (
        <ReleaseChangelogSection changelogSummary={summary} collapsible={collapsibleChangelog} />
      ),
    });
  }

  return (
    <div className={cn('space-y-8', className)} {...rest}>
      {sections.map((section) => (
        <div key={section.key}>{section.node}</div>
      ))}
    </div>
  );
}
